import { Item, ItemGroup, ItemType, QuoteType, Version } from "src/api-service";
import { QuoteState } from "../quote.state";
import {
    QuoteValidation,
    QuoteLevelValidation,
    VersionLevelValidation,
    emptyQuoteValidation,
    emptyQuoteLevelValidation,
    emptyVersionLevelValidation,
} from "./validation.types";
import { decideCreditCheck, isCreditCheckBlocking } from "src/app/store/application/data/credit-check/quote/decide-credit-check";

// **************************************************************************
// PURE VALIDATION FUNCTIONS
// **************************************************************************

/**
 * Validates a quote for order creation.
 * Pure function - returns error keys, not translated messages.
 */
export function validateQuoteForOrder(
    quote: QuoteState | null | undefined,
    version: Version | null | undefined,
    selectedCompanyId?: string | null,
): QuoteValidation {
    if (!quote) {
        return emptyQuoteValidation;
    }

    const quoteValidation: QuoteLevelValidation = {
        ...emptyQuoteLevelValidation,
        customerErrors: [],
        quoteErrors: [],
        deliveryErrors: [],
        salesSupportErrors: [],
        salesCreditErrors: [],
    };

    const versionValidation: VersionLevelValidation = {
        ...emptyVersionLevelValidation,
        shippingErrors: [],
        projectErrors: [],
        itemGroupErrors: [],
        itemErrors: [],
        subscriptionErrors: [],
        itemWarnings: [],
    };

    // ========================================
    // QUOTE-LEVEL VALIDATIONS
    // ========================================
    validateCustomer(quote, quoteValidation, selectedCompanyId);
    validateQuoteInfo(quote, quoteValidation);
    validateDelivery(quote, quoteValidation);
    validateSalesSupport(quote, quoteValidation);
    validateSalesCredit(quote, quoteValidation);

    // ========================================
    // VERSION-LEVEL VALIDATIONS
    // ========================================
    if (version) {
        validateShipping(version, versionValidation);
        // Project management isn't part of the subscription flow — skip the gate there.
        if (quote.type !== QuoteType.Subscription) {
            validateProject(version, versionValidation);
        }
        validateItemGroups(version, versionValidation);
        if (quote.type === QuoteType.Subscription) {
            validateSubscription(version, versionValidation);
        }
    }

    // ========================================
    // CREDIT CHECK VALIDATION
    // ========================================
    if (quote.deliveryInfo?.paymentTerm?.requiresCreditCheck && version) {
        const totalIncVat = version.summary?.totalSalesPriceIncVat ?? 0;
        const outcome = decideCreditCheck(
            quote.creditCheck,
            quote.customer?.code,
            quote.customer?.registrationNo,
            totalIncVat,
        );
        if (isCreditCheckBlocking(outcome, quote.creditCheck)) {
            quoteValidation.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_CREDIT_CHECK_DENIED" });
        }
    }

    // ========================================
    // CALCULATE TOTALS
    // ========================================
    const totalQuoteErrors =
        quoteValidation.customerErrors.length +
        quoteValidation.quoteErrors.length +
        quoteValidation.deliveryErrors.length +
        quoteValidation.salesCreditErrors.length;
    // Note: salesSupportErrors NOT included (only blocks "send to review")

    const totalVersionErrors =
        versionValidation.shippingErrors.length +
        versionValidation.projectErrors.length +
        versionValidation.itemGroupErrors.length +
        versionValidation.itemErrors.length +
        versionValidation.subscriptionErrors.length;

    const totalOrderErrorCount = totalQuoteErrors + totalVersionErrors;
    const totalWarningCount = versionValidation.itemWarnings.length;

    return {
        quoteValidation,
        versionValidation,
        totalQuoteErrors,
        totalVersionErrors,
        totalOrderErrorCount,
        totalWarningCount,
        isValidForOrder: totalOrderErrorCount === 0,
    };
}

// **************************************************************************
// QUOTE-LEVEL VALIDATION HELPERS
// **************************************************************************

function validateCustomer(
    quote: QuoteState,
    result: QuoteLevelValidation,
    selectedCompanyId?: string | null,
): void {
    const customer = quote.customer;

    if (customer?.deleted) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_CUSTOMER_DELETED" });
    }
    if (!customer?.name) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_CUSTOMER_NAME" });
    }
    if (!customer?.customerTypeId) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_CUSTOMER_TYPE" });
    }
    if (!customer?.billingAddress?.addressLine) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_BILLING_ADDRESS" });
    }
    if (!customer?.billingAddress?.city) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_BILLING_CITY" });
    }
    if (!customer?.billingAddress?.countryCode) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_BILLING_COUNTRY" });
    }
    if (!customer?.billingAddress?.recipientName) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_BILLING_RECIPIENT" });
    }
    if (!customer?.billingAddress?.zipCode) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_BILLING_ZIP" });
    }
    if (!customer?.billingEmail) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_BILLING_EMAIL" });
    }

    if (!quote.deliveryInfo?.paymentTerm?.code) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_PAYMENT_TERM" });
    }

    // Leasing validation
    if (quote.deliveryInfo?.paymentTerm?.code === "LEASING" && !quote.leasingInfo?.financingCompany?.code) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_FINANCING_COMPANY" });
    }

    // Contract reference number is required for subscription quotes.
    // Grouped with the billing errors since the field lives on the billing tab.
    if (quote.type === QuoteType.Subscription && !quote.salesInfo?.contractReferenceNumber) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_SUBSCRIPTION_CONTRACT_REFERENCE" });
    }

    // Customer reference is required when the selling company is Swedish (id starts with "31")
    // and when the offer is against a Swedish customer.
    const isSwedishCompany = selectedCompanyId?.startsWith("31") ?? false;
    const isSwedishCustomer = customer?.billingAddress?.countryCode === "SE";
    if ((isSwedishCompany && isSwedishCustomer) && !quote.salesInfo?.customerReference) {
        result.customerErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_CUSTOMER_REFERENCE" });
    }
}

function validateQuoteInfo(quote: QuoteState, result: QuoteLevelValidation): void {
    if (!quote.salesInfo?.responsibleSalesPerson?.code) {
        result.quoteErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_SALES_PERSON" });
    }

    if (!quote.opportunity?.name) {
        result.quoteErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_OPPORTUNITY_NAME" });
    }

    if (!quote.salesInfo?.expectedOrderMonth) {
        result.quoteErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_EXPECTED_ORDER_MONTH" });
    }

    // Contact validation
    if (!quote.salesInfo?.contactName) {
        result.quoteErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_CONTACT_NAME" });
    }
    if (!quote.salesInfo?.contactPhone && !quote.salesInfo?.contactEmail) {
        result.quoteErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_CUSTOMER_TELEPHONE_OR_EMAIL" });
    }
}

/**
 * Validates quote-level delivery information (quote.deliveryInfo)
 */
function validateDelivery(quote: QuoteState, result: QuoteLevelValidation): void {
    const delivery = quote.deliveryInfo;
    const useBilling = delivery?.useBillingAddressForDelivery;

    // Address validation depends on whether we use billing address
    if (!useBilling) {
        if (!delivery?.address?.addressLine) {
            result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_ADDRESS" });
        }
        if (!delivery?.address?.zipCode) {
            result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_ZIP" });
        }
        if (!delivery?.address?.city) {
            result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_CITY" });
        }
    } else {
        // Using billing address - check billing instead
        const billingAddress = quote.customer?.billingAddress;
        if (!billingAddress?.addressLine) {
            result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_ADDRESS" });
        }
        if (!billingAddress?.zipCode) {
            result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_ZIP" });
        }
        if (!billingAddress?.city) {
            result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_CITY" });
        }
    }

    if (!delivery?.address?.countryCode) {
        result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_COUNTRY" });
    }
    if (!delivery?.address?.recipientName) {
        result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_RECIPIENT" });
    }

    if (!delivery?.deliveryTerm?.code) {
        result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_TERM" });
    }
    if (!delivery?.location?.code) {
        result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_LOCATION" });
    }
    if (!delivery?.deliveryDate) {
        result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_DATE" });
    }

    // Check if delivery date is in the past
    if (delivery?.deliveryDate) {
        const todaysDate = new Date();
        const deliveryDate = new Date(delivery.deliveryDate);
        todaysDate.setHours(0, 0, 0, 0);
        deliveryDate.setHours(0, 0, 0, 0);

        if (deliveryDate < todaysDate) {
            result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_DATE_PAST" });
        }
    }

    if (!delivery?.deliveryContact) {
        result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_CONTACT" });
    }
    if (!delivery?.deliveryPhone || !isValidDeliveryPhone(delivery.deliveryPhone)) {
        result.deliveryErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_DELIVERY_PHONE" });
    }

}

function validateSalesSupport(quote: QuoteState, result: QuoteLevelValidation): void {
    if (!quote.salesInfo?.orderInformationComment) {
        result.salesSupportErrors.push({
            key: "SEND_ORDER_TO_REVIEW.VALIDATION.ERR_ORDER_INFORMATION",
        });
    }
    if (quote.salesInfo?.attachmentsExist && !quote.salesInfo?.attachmentsNotes) {
        result.salesSupportErrors.push({
            key: "SEND_ORDER_TO_REVIEW.VALIDATION.ERR_ATTACHMENT_NOTES",
        });
    }
    if (!quote.deliveryInfo?.desiredDeliveryWeek) {
        result.salesSupportErrors.push({
            key: "SEND_ORDER_TO_REVIEW.VALIDATION.ERR_DELIVERY_DESIRED_WEEK_EMPTY",
        });
    }
    if (!quote.salesInfo?.expectedOrderMonth) {
        result.salesSupportErrors.push({
            key: "SEND_ORDER_TO_REVIEW.VALIDATION.ERR_EXPECTED_ORDER_MONTH_EMPTY",
        });
    }

    // Check if delivery date is in the past
    if (quote.deliveryInfo?.deliveryDate) {
        const todaysDate = new Date();
        const deliveryDate = new Date(quote.deliveryInfo.deliveryDate);
        todaysDate.setHours(0, 0, 0, 0);
        deliveryDate.setHours(0, 0, 0, 0);

        if (deliveryDate < todaysDate) {
            result.salesSupportErrors.push({
                key: "SEND_ORDER_TO_REVIEW.VALIDATION.ERR_DELIVERY_DATE_PAST",
            });
        }
    }
}

function validateSalesCredit(quote: QuoteState, result: QuoteLevelValidation): void {
    const rows = quote.salesInfo?.salesCredits ?? [];

    if (rows.length === 0) {
        result.salesCreditErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_SALES_CREDIT_EMPTY" });
        return;
    }

    // Integer hundredths, same trick as sales-team.component.totalShareHundredths:
    // 33.10 + 33.45 + 33.45 as doubles is 100.00000000000001, which would incorrectly
    // fail an equality check against a valid 100% split.
    const totalHundredths = rows.reduce(
        (sum, row) => sum + Math.round((Number(row.creditShare) || 0) * 100),
        0,
    );
    if (totalHundredths !== 10000) {
        result.salesCreditErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_SALES_CREDIT_NOT_100",
            params: { total: totalHundredths / 100 },
        });
    }

    // Duplicate salesperson: guarded in the UI, but a stale cached quote could still surface it.
    const people = rows.map(r => r.salesPersonId);
    if (new Set(people).size !== people.length) {
        result.salesCreditErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_SALES_CREDIT_DUPLICATE" });
    }

    for (const row of rows) {
        if (!row.salesPersonId || !row.companyId || (row.salesCreditRoleIds?.length ?? 0) === 0) {
            result.salesCreditErrors.push({
                key: "CREATE_ORDER.VALIDATION.ERR_SALES_CREDIT_INCOMPLETE_ROW",
                params: { name: row.salesPerson?.name ?? "" },
            });
        }
    }
}

function isValidDeliveryPhone(phone: string): boolean {
    const v = phone.trim();
    if (!v) return false;
    const digitsOnly = v.replace(/[()\s.\-]/g, "").replace(/^\+/, "");
    return /^\d{7,15}$/.test(digitsOnly) && (v.startsWith("+") || !/\+/.test(v));
}

// **************************************************************************
// VERSION-LEVEL VALIDATION HELPERS
// **************************************************************************

/**
 * Validates version-level shipping information (version.shipping)
 * Placeholder for future shipping cost validations
 */
function validateShipping(_version: Version, _result: VersionLevelValidation): void {
    // Currently no validations needed for version.shipping
    // This is a placeholder for future shipping cost validations
}

/**
 * Validates version-level project management and kickback (version.projectManagementAndKickback)
 */
function validateProject(version: Version, result: VersionLevelValidation): void {
    const includeProjectManagement = version.projectManagementAndKickback?.includeProjectManagementCost;
    const projectRevenue = version.summary?.projectManagementCostExVat ?? 0;

    if (includeProjectManagement && projectRevenue === 0) {
        result.projectErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_PROJECT_MANAGEMENT_REQUIRED" });
    }
}

function validateSubscription(version: Version, result: VersionLevelValidation): void {
    const subscription = version.subscription;

    if (!subscription?.squareMeters || subscription.squareMeters <= 0) {
        result.subscriptionErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_SUBSCRIPTION_SQUARE_METERS" });
    }
    if (!subscription?.occupantCount || subscription.occupantCount <= 0) {
        result.subscriptionErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_SUBSCRIPTION_OCCUPANT_COUNT" });
    }

    if (!subscription?.financingCompanyId) {
        result.subscriptionErrors.push({ key: "CREATE_ORDER.VALIDATION.ERR_SUBSCRIPTION_FINANCING_COMPANY" });
    }
}

function validateItemGroups(version: Version, result: VersionLevelValidation): void {
    const groups = version.itemGroups ?? [];
    let itemsWithZeroPrice = 0;

    for (const group of groups) {
        // Group: isDraft
        if (group.isDraft) {
            result.itemGroupErrors.push({
                key: "CREATE_ORDER.VALIDATION.ERR_GROUP_DRAFT",
                params: { groupName: group.name || `Grupp ${group.id}` },
            });
        }

        // Group: name required
        if (!group.name) {
            result.itemGroupErrors.push({
                key: "CREATE_ORDER.VALIDATION.ERR_GROUP_NAME",
                params: { groupId: group.id },
            });
        }

        // Validate items within this group
        for (const item of group.items ?? []) {
            validateItem(item, group, result);

            // Count items with zero prices (for warning)
            // Use Number() === 0 to handle both 0 and "0", and check that it's not null/undefined
            const hasZeroVendorPrice = item.vendorPrice != null && Number(item.vendorPrice) === 0;
            const hasZeroUnitCost = item.unitCost != null && Number(item.unitCost) === 0;
            const hasZeroSalesUnitPrice = item.salesUnitPriceExVat != null && Number(item.salesUnitPriceExVat) === 0;

            if (!item.isDraft && (hasZeroVendorPrice || hasZeroUnitCost || hasZeroSalesUnitPrice)) {
                itemsWithZeroPrice++;
            }
        }
    }

    // Add a single warning if any items have zero prices
    if (itemsWithZeroPrice > 0) {
        result.itemWarnings.push({
            key: "CREATE_ORDER.VALIDATION.WARN_ITEMS_ZERO_PRICE",
            params: { count: itemsWithZeroPrice },
        });
    }
}

function validateItem(item: Item, group: ItemGroup, result: VersionLevelValidation): void {
    const groupName = group.name || `Grupp ${group.id}`;
    const itemContext = {
        itemId: item.id,
        itemName: item.name,
        groupName,
    };

    // Item: isDraft
    if (item.isDraft) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_DRAFT",
            params: itemContext,
        });
        return; // Skip other validations for draft items
    }

    if (!item.id || item.id < 0) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_UNSAVED",
            params: itemContext,
        });
        return;
    }
    if (!item.name) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_NAME",
            params: itemContext,
        });
    }
    if (!item.quantity || item.quantity < 1) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_QUANTITY",
            params: itemContext,
        });
    }
    if (item.unitCost == null || item.unitCost < 0) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_COST",
            params: itemContext,
        });
    }
    if (item.unitPriceExVat == null || item.unitPriceExVat < 0 || item.unitPriceIncVat == null || item.unitPriceIncVat < 0) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_UNIT_PRICE",
            params: itemContext,
        });
    }
    if (item.vendorPrice == null || item.vendorPrice < 0) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_LIST_PRICE",
            params: itemContext,
        });
    }
    if (item.brand?.localPriceList && (item.vendorWholesalePrice == null || item.vendorWholesalePrice < 0)) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_PURCHASE_PRICE",
            params: itemContext,
        });
    }
    if (item.littera && item.littera.length > 50 && (item.itemType === ItemType.Item || item.itemType === ItemType.SubItem)) {
        result.itemErrors.push({
            key: "CREATE_ORDER.VALIDATION.ERR_ITEM_LITTERA_TOO_LONG",
            params: { ...itemContext, maxLength: 50 },
        });
    }
}
